import { EFFECTIVE_FULL_COMPLETION_THRESHOLD } from '@/utils/is-effectively-completed-workout';
import { getWorkoutCompletionPercentage } from './get-workout-completion-percentage';
import type { PlayerExercise } from '../types';

const clampPercentage = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
};

export function calculateActiveTimePercentage(activeDurationMs: number, plannedActiveDurationMs: number): number {
  if (!(plannedActiveDurationMs > 0) || !Number.isFinite(plannedActiveDurationMs)) {
    return 0;
  }
  const active = Math.max(0, Number.isFinite(activeDurationMs) ? activeDurationMs : 0);
  return clampPercentage((active / plannedActiveDurationMs) * 100);
}

/** Same gate as backend: min(progress, activeTime). */
export function estimateRewardCompletionPercentage({
  progressPercentage,
  exerciseActiveDurationMs,
  plannedExerciseOnlyMs,
}: {
  progressPercentage: number;
  exerciseActiveDurationMs: number;
  plannedExerciseOnlyMs: number;
}): number {
  const activeTimePercentage = calculateActiveTimePercentage(exerciseActiveDurationMs, plannedExerciseOnlyMs);
  return Math.min(clampPercentage(progressPercentage), activeTimePercentage);
}

export function getPlannedExerciseOnlyMs(exercises: PlayerExercise[]): number {
  return exercises.reduce((sum, exercise) => sum + Math.max(0, exercise.time || 0), 0);
}

export function shouldUseEarlyExitFlow(rewardCompletionPercentage: number): boolean {
  return clampPercentage(rewardCompletionPercentage) < EFFECTIVE_FULL_COMPLETION_THRESHOLD;
}

export function resolveEarlyExitDecision({
  exercises,
  exerciseIdx,
  elapsedTimeMs,
  exerciseActiveDurationMs,
}: {
  exercises: PlayerExercise[];
  exerciseIdx: number;
  elapsedTimeMs: number;
  exerciseActiveDurationMs: number;
}): { completePercentage: number; rewardCompletionPercentage: number; useEarlyExit: boolean } {
  const completePercentage = getWorkoutCompletionPercentage(exercises, exerciseIdx, elapsedTimeMs);
  const rewardCompletionPercentage = estimateRewardCompletionPercentage({
    progressPercentage: completePercentage,
    exerciseActiveDurationMs,
    plannedExerciseOnlyMs: getPlannedExerciseOnlyMs(exercises),
  });
  return {
    completePercentage,
    rewardCompletionPercentage,
    useEarlyExit: shouldUseEarlyExitFlow(rewardCompletionPercentage),
  };
}
